import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  ButtonGroup,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { encode } from "base64-arraybuffer";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { pdfHeader, pdfFooter, pdfTableStyles } from "../../../pdfUtils";

const ITEMS_PER_PAGE = 50;
const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const safeText = (value) => (value === null || value === undefined ? "" : String(value).trim());
const safeLower = (value) => safeText(value).toLowerCase();
const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const includesSafe = (value, term) => safeLower(value).includes(safeLower(term));
const fmtNum = (v) => new Intl.NumberFormat("en-US").format(safeNumber(v));
const fmtMoney = (v) => "$" + new Intl.NumberFormat("en-US").format(safeNumber(v));
const fmtMoneyCorto = (v) => {
  const n = safeNumber(v);
  if (Math.abs(n) >= 1e6) return "$" + (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + "M";
  if (Math.abs(n) >= 1e3) return "$" + (n / 1e3).toFixed(0) + "K";
  return fmtMoney(n);
};
const pct = (a, b) => (b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0);

// Atajos de periodo
const PRESETS = [
  { label: "Este mes", rango: () => [moment().startOf("month"), moment()] },
  { label: "3 meses", rango: () => [moment().subtract(91, "days"), moment()] },
  { label: "Este año", rango: () => [moment().startOf("year"), moment()] },
  { label: "Año anterior", rango: () => [moment().subtract(1, "year").startOf("year"), moment().subtract(1, "year").endOf("year")] },
];

function ReporteContenedores() {
  const { user } = useContext(AuthContext);
  const URL_CONTENEDORES = process.env.REACT_APP_URL_CONTENEDORES;
  const URL_ARTICULOS_CONTENEDOR = process.env.REACT_APP_URL_ARTICULOS_CONTENEDOR;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const URL_BROKERS = process.env.REACT_APP_URL_BROKERS;
  const URL_SEND_MAIL_ATT = process.env.REACT_APP_URL_SEND_MAIL_ATT;
  const [t] = useTranslation("global");

  const endDate = moment().format("YYYY-MM-DD");
  const startDate = moment().subtract(91, "days").format("YYYY-MM-DD");

  const [clientes, setClientes] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedBroker, setSelectedBroker] = useState("");
  const [selectedEntregado, setSelectedEntregado] = useState("");
  const [selectedFacturado, setSelectedFacturado] = useState("");
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);

  const [photo, setPhoto] = useState([]);
  const [modalFoto, setModalFoto] = useState(false);
  const toggleFoto = () => setModalFoto(!modalFoto);

  const [gastos, setGastos] = useState([]);
  const [modalGastos, setModalGastos] = useState(false);
  const toggleGastos = () => setModalGastos(!modalGastos);

  const [modalMail, setModalMail] = useState(false);
  const toggleMail = () => setModalMail(!modalMail);
  const [mailTo, setMailTo] = useState("");

  const [comments, setComments] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState([]);
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const headersAuth = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });

  // Catálogos: una sola vez (antes se volvían a pedir en cada cambio de fecha)
  useEffect(() => {
    let vivo = true;
    Promise.all([
      axios.get(`${URL_CLIENTES}`, { headers: headersAuth() }),
      axios.get(`${URL_BROKERS}`, { headers: headersAuth() }),
    ])
      .then(([cliRes, brokRes]) => {
        if (!vivo) return;
        setClientes(Array.isArray(cliRes.data) ? cliRes.data : []);
        setBrokers(Array.isArray(brokRes.data) ? brokRes.data : []);
      })
      .catch((err) => console.log(err));
    return () => { vivo = false; };
  }, []); // eslint-disable-line

  // Reporte del periodo: el back ya manda solo los campos necesarios y el surtido de cada contenedor
  useEffect(() => {
    let vivo = true;
    setCargando(true);
    axios
      .get(`${URL_CONTENEDORES}FechasReporte/${selectedFechaInicio}/${selectedFechaFin}`, { headers: headersAuth() })
      .then((contRes) => {
        if (!vivo) return;
        const allContenedores = Array.isArray(contRes.data) ? contRes.data : [];
        const arrayTabla = [...allContenedores]
          .sort((a, b) => safeText(b?.idContenedor).localeCompare(safeText(a?.idContenedor)))
          .map((a) => {
            const articulos = Array.isArray(a?.articulos) ? a.articulos : [];
            const articulosResumen = articulos
              .map((x) => {
                const nombre = safeText(x?.nombre || x?.name || "NA");
                const cantidad = safeNumber(x?.cantidad);
                return cantidad ? `${nombre} x ${cantidad}` : nombre;
              })
              .join(", ");

            return {
              _id: a?._id || "",
              numero: safeText(a?.idContenedor),
              activo: safeText(a?.is_active),
              fecha: safeText(a?.fecha),
              observaciones: safeText(a?.observaciones),
              totalGeneral: safeNumber(a?.totalGeneral),
              naviera: safeText(a?.navieras?.[0]?.name || "NA"),
              idNaviera: safeText(a?.navieras?.[0]?._id),
              clientes: safeText(a?.clientes?.[0]?.nombre_comercial || "NA"),
              idClientes: safeText(a?.clientes?.[0]?._id),
              brokers: safeText(a?.brokers?.[0]?.name || "NA"),
              idBrokers: safeText(a?.brokers?.[0]?._id),
              forwarders: safeText(a?.forwarders?.[0]?.name || "NA"),
              idForwarders: safeText(a?.forwarders?.[0]?._id),
              totalCantidad: safeNumber(a?.totalCantidad),
              totalSurtido: safeNumber(a?.totalSurtido),
              pendienteSurtir: safeNumber(a?.pendienteSurtir),
              numeroReferencia: safeText(a?.numeroReferencia),
              referenciaExtra: safeText(a?.referenciaExtra),
              flete: safeNumber(a?.flete),
              referencia: safeText(a?.referencia),
              etd: safeText(a?.etd),
              etaEstimada: safeText(a?.etaEstimada),
              etaReal: safeText(a?.etaReal),
              bl: safeText(a?.bl),
              pl: safeText(a?.pl),
              totalGastos: safeNumber(a?.totalGastos),
              fechaLlegada: safeText(a?.fechaLlegada),
              fechaLiberacion: safeText(a?.fechaLiberacion),
              fechaEntregaEstimada: safeText(a?.fechaEntregaEstimada),
              fechaEntregaReal: safeText(a?.fechaEntregaReal),
              aduana: safeText(a?.aduana),
              montoServicio: safeNumber(a?.montoServicio),
              saldo: safeNumber(a?.saldo),
              pagos: safeNumber(a?.montoServicio) - safeNumber(a?.saldo),
              fechaPago: safeText(a?.fechaPago),
              facturado: safeText(a?.facturado),
              facturadoFacturas:
                safeText(a?.facturado) === "Si" ? `Si ${safeText(a?.cantidadFacturas)}` : safeText(a?.facturado),
              fechaPagoGasto: safeText(a?.fechaPagoGasto),
              surtidoId: a?.surtidoId || "",
              entregado: safeText(a?.surtido),
              articulos,
              articulosResumen,
            };
          });
        setComments(arrayTabla);
      })
      .catch((err) => {
        console.log(err);
        if (vivo) setComments([]);
      })
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, [selectedFechaInicio, selectedFechaFin]); // eslint-disable-line

  // Todo lo filtrado (lo que se ve, lo que se exporta y de donde salen los totales y las gráficas)
  const filtrados = useMemo(() => {
    let computed = [...comments];
    const terms = Array.isArray(search) ? search.filter((term) => safeText(term) !== "") : [];

    if (terms.length > 0) {
      computed = computed.filter((c) =>
        terms.some((term) =>
          includesSafe(c.numero, term) ||
          includesSafe(c.clientes, term) ||
          includesSafe(c.numeroReferencia, term) ||
          includesSafe(c.referenciaExtra, term) ||
          includesSafe(c.referencia, term) ||
          includesSafe(c.forwarders, term) ||
          includesSafe(c.brokers, term) ||
          includesSafe(c.facturadoFacturas, term) ||
          includesSafe(c.articulosResumen, term) ||
          safeText(c.totalCantidad).includes(safeText(term)) ||
          safeText(c.totalGastos).includes(safeText(term)) ||
          safeText(c.montoServicio).includes(safeText(term)) ||
          safeText(c.flete).includes(safeText(term)) ||
          safeText(c.saldo).includes(safeText(term))
        )
      );
    }
    if (selectedFechaInicio) computed = computed.filter((e) => e.fecha >= selectedFechaInicio);
    if (selectedFechaFin) computed = computed.filter((e) => e.fecha <= selectedFechaFin);
    if (selectedFacturado) computed = computed.filter((e) => includesSafe(e.facturado, selectedFacturado));
    if (selectedCliente) computed = computed.filter((e) => e.idClientes === selectedCliente);
    if (selectedBroker) computed = computed.filter((e) => e.idBrokers === selectedBroker);
    if (selectedEntregado) computed = computed.filter((e) => includesSafe(e.entregado, selectedEntregado));

    if (sorting.field) {
      const numericFields = ["totalCantidad", "flete", "totalGastos", "montoServicio", "saldo", "pagos", "numero"];
      const dir = sorting.order === "asc" ? 1 : -1;
      computed.sort((a, b) =>
        numericFields.includes(sorting.field)
          ? (safeNumber(a[sorting.field]) - safeNumber(b[sorting.field])) * dir
          : safeText(a[sorting.field]).localeCompare(safeText(b[sorting.field])) * dir
      );
    }
    return computed;
  }, [comments, search, sorting, selectedFechaInicio, selectedFechaFin, selectedFacturado, selectedCliente, selectedBroker, selectedEntregado]);

  const totalItems = filtrados.length;
  const commentsData = useMemo(
    () => filtrados.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filtrados, currentPage]
  );

  // Indicadores y gráficas (sobre lo filtrado)
  const resumen = useMemo(() => {
    const r = { servicio: 0, gastos: 0, pagos: 0, saldo: 0, piezas: 0, flete: 0, entregados: 0 };
    const porCliente = new Map();
    const porMes = new Map();
    for (const c of filtrados) {
      r.servicio += c.montoServicio;
      r.gastos += c.totalGastos;
      r.pagos += c.pagos;
      r.saldo += c.saldo;
      r.piezas += c.totalCantidad;
      r.flete += c.flete;
      if (c.entregado === "Si") r.entregados += 1;
      const pc = porCliente.get(c.clientes) || { n: 0, monto: 0 };
      porCliente.set(c.clientes, { n: pc.n + 1, monto: pc.monto + c.montoServicio });
      const mes = c.fecha.slice(0, 7);
      if (mes) porMes.set(mes, (porMes.get(mes) || 0) + 1);
    }
    r.topClientes = [...porCliente.entries()].sort((a, b) => b[1].n - a[1].n || b[1].monto - a[1].monto).slice(0, 6);
    r.meses = [...porMes.entries()].sort((a, b) => a[0].localeCompare(b[0])).slice(-12);
    return r;
  }, [filtrados]);

  const enProceso = totalItems - resumen.entregados;
  const pctEntregados = pct(resumen.entregados, totalItems);
  const pctCobrado = pct(resumen.pagos, resumen.servicio);
  const maxCliente = resumen.topClientes.length ? resumen.topClientes[0][1].n : 0;
  const maxMes = resumen.meses.reduce((m, [, n]) => Math.max(m, n), 0);

  const hayFiltros = !!(selectedCliente || selectedBroker || selectedFacturado || selectedEntregado || (search && search.length));
  const limpiarFiltros = () => {
    setSelectedCliente("");
    setSelectedBroker("");
    setSelectedFacturado("");
    setSelectedEntregado("");
    setCurrentPage(1);
  };
  const aplicarPreset = (p) => {
    const [ini, fin] = p.rango();
    setSelectedFechaInicio(ini.format("YYYY-MM-DD"));
    setSelectedFechaFin(fin.format("YYYY-MM-DD"));
    setCurrentPage(1);
  };
  const presetActivo = (p) => {
    const [ini, fin] = p.rango();
    return ini.format("YYYY-MM-DD") === selectedFechaInicio && fin.format("YYYY-MM-DD") === selectedFechaFin;
  };

  // ── Exportaciones: exportan lo filtrado en pantalla ─────────────────────────
  const filasPDF = (conRefExtra) =>
    filtrados.map((a) => [
      a.numero,
      a.fecha,
      a.numeroReferencia,
      ...(conRefExtra ? [a.referenciaExtra] : []),
      a.referencia,
      a.clientes,
      a.forwarders,
      a.brokers,
      fmtNum(a.totalCantidad),
      a.etd,
      a.etaEstimada,
      a.etaReal,
      a.fechaLiberacion,
      a.fechaEntregaReal,
      fmtMoney(a.flete),
      fmtMoney(a.montoServicio),
      fmtMoney(a.totalGastos),
    ]);
  const headPDF = (conRefExtra) => [[
    "Numero", "Fecha", "No. Ref", ...(conRefExtra ? ["Referencia"] : []), "Contenedor", "Cliente", "Forwarder",
    "Brokers", "Cantidad", "ETD", "ETA Est", "ETA Real", "Fecha Lib", "Fecha Entrega", "Flete", "Monto Servicio", "Gastos",
  ]];
  const piePDF = (conRefExtra) => [[
    ...Array(conRefExtra ? 14 : 13).fill(""), "Totales", fmtMoney(resumen.servicio), fmtMoney(resumen.gastos),
  ]];
  const docListado = (conRefExtra) => {
    const doc = new jsPDF("landscape");
    const startY = pdfHeader(
      doc,
      "Reporte de Contenedores",
      `${selectedFechaInicio} al ${selectedFechaFin} · ${fmtNum(totalItems)} contenedores · Facturado ${fmtMoney(resumen.servicio)}`
    );
    doc.autoTable({
      ...pdfTableStyles({ fontSize: 6.5 }, { styles: { ...pdfTableStyles().styles, fontSize: 6.5, cellPadding: 1.6 } }),
      head: headPDF(conRefExtra),
      body: filasPDF(conRefExtra),
      foot: piePDF(conRefExtra),
      showFoot: "lastPage",
      startY,
    });
    pdfFooter(doc);
    return doc;
  };

  function PDFTabla() {
    docListado(true).save(`Contenedores.pdf`);
  }

  function excel() {
    const dataExcel = filtrados.map((a) => ({
      Numero: a.numero,
      Fecha: a.fecha,
      NoRef: a.numeroReferencia,
      RefExtra: a.referenciaExtra,
      Referencia: a.referencia,
      Cliente: a.clientes,
      Forwarder: a.forwarders,
      Brocker: a.brokers,
      Cantidad: fmtNum(a.totalCantidad),
      ETD: a.etd,
      ETAEst: a.etaEstimada,
      ETAReal: a.etaReal,
      FechaLib: a.fechaLiberacion,
      FechaEntrega: a.fechaEntregaReal,
      Flete: fmtMoney(a.flete),
      MontoServicio: fmtMoney(a.montoServicio),
      Gasto: fmtMoney(a.totalGastos),
    }));
    const columnas = ["Numero", "Fecha", "NoRef", "RefExtra", "Referencia", "Cliente", "Forwarder", "Brocker", "Cantidad",
      "ETD", "ETAEst", "ETAReal", "FechaLib", "FechaEntrega", "Flete", "MontoServicio", "Gasto"];
    const option = {
      fileName: "ReporteContenedores",
      datas: [{ sheetData: dataExcel, sheetName: "ReporteContenedores", sheetFilter: columnas, sheetHeader: columnas }],
    };
    new ExportJsonExcel(option).saveExcel();
  }

  function enviaMail() {
    const doc = docListado(false);
    const base64File = encode(doc.output("arraybuffer"));

    axios
      .post(
        URL_SEND_MAIL_ATT,
        {
          subject: "Listado Contenedores",
          email: mailTo,
          fileName: "ReporteContenedores.pdf",
          att: base64File,
          text: `<table border="0" cellpadding="0" cellspacing="0" width="100%">
         <th><img width="${process.env.REACT_APP_TAMANO_LOGO_MAIL}" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}logoInstitucion.png" /></th>
         <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${process.env.REACT_APP_NOMBRE_INSTITUCION}</tr>
         <tbody>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Hola,</tr>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Adjunto encontraras el listado de Contenedores.</tr>
         </tbody>
         <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo, para cualquier duda favor de comunicarse al ${process.env.REACT_APP_TELEFONO_INSTITUCION}</tr>
         <tr align="right"><br>Powered by <a href="https://lynqro.com"> <img width="20%" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}Logo-Lynqro-PDF.png" /> </a> </tr>
         </table>`,
        },
        { headers: headersAuth() }
      )
      .then(() => {
        toggleMail();
        Swal.fire("Good job!", "Enviado con exito", "success");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
          footer: `${error.response ? error.response.data : error.message}`,
        });
        console.log(error);
      });
  }

  async function PDFOC(fecha, totalCantidad, numero, naviera, idPDFOC) {
    await axios
      .get(`${URL_ARTICULOS_CONTENEDOR}/contenedor/${idPDFOC}`, { headers: headersAuth() })
      .then((response) => {
        const filas = (response.data || [])
          .filter((a) => a)
          .map((a) => [a.articulos[0].name, a.descripcion, fmtNum(a.cantidad)]);
        const doc = new jsPDF("landscape");
        const startY = pdfHeader(
          doc,
          `Contenedor # ${numero}`,
          `Fecha ${fecha} · Naviera ${naviera} · Piezas ${fmtNum(totalCantidad)}`
        );
        doc.autoTable({
          ...pdfTableStyles(),
          head: [["Articulo", "Descripcion", "Cantidad"]],
          body: filas,
          startY,
        });
        pdfFooter(doc);
        window.open(doc.output("bloburl"), "_blank");
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function jalaFoto(idCont, idSurt) {
    setPhoto([]);
    const token = localStorage.getItem("app_token");
    const pedir = (id) =>
      axios.get(`${process.env.REACT_APP_URL_FINDMEDIABYTAG}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    const requests = [pedir(idCont), ...(idSurt ? [pedir(idSurt)] : [])];

    Promise.all(requests)
      .then((responses) => {
        const recursos = (r) => (Array.isArray(r?.data?.resources) ? r.data.resources : []);
        setPhoto(responses.flatMap(recursos));
      })
      .catch((err) => {
        console.log("Error:", err);
        setPhoto([]);
      });

    toggleFoto();
  }

  function jalaGastos(idCont) {
    axios
      .get(`${process.env.REACT_APP_URL_GASTOS_CONTENEDOR}Contenedor/${idCont}`, { headers: headersAuth() })
      .then((response) => {
        setGastos(response.data);
        toggleGastos();
      })
      .catch((err) => console.log(err));
  }

  const headers = [
    { name: `${t("Fecha")}`, field: "fecha", sortable: true },
    { name: `${t("No. Referencia")}`, field: "numeroReferencia", sortable: true },
    { name: `${t("Ref Extra")}`, field: "referenciaExtra", sortable: true },
    { name: `${t("Contenedor")}`, field: "referencia", sortable: true },
    { name: `${t("Estatus")}`, field: "entregado", sortable: true },
    { name: `${t("Cliente")}`, field: "clientes", sortable: true },
    { name: `${t("Forwarder")}`, field: "forwarders", sortable: true },
    { name: `${t("ETD")}`, field: "etd", sortable: true },
    { name: `${t("ETA Puerto")}`, field: "etaReal", sortable: true },
    { name: `${t("Broker")}`, field: "brokers", sortable: true },
    { name: `${t("Cantidad")}`, field: "totalCantidad", sortable: true },
    { name: `${t("Articulo")}`, field: "articulosResumen", sortable: true },
    { name: `${t("Entrega")}`, field: "fechaEntregaReal", sortable: true },
    { name: `${t("Flete")}`, field: "flete", sortable: true },
    { name: `${t("Costos Imp")}`, field: "totalGastos", sortable: true },
    { name: `${t("Facturado")}`, field: "facturadoFacturas", sortable: true },
    { name: `${t("Monto Facturado")}`, field: "montoServicio", sortable: true },
    { name: `${t("Pagos")}`, field: "pagos", sortable: true },
    { name: `${t("Fecha Pago")}`, field: "fechaPago", sortable: true },
    { name: `${t("Saldo")}`, field: "saldo", sortable: true },
    { name: `${t("Det.Piezas")}`, field: "pdf", sortable: false },
    { name: `${t("Archivos")}`, field: "archivos", sortable: false },
  ];

  const sinFecha = (v) => !v || v === "NA" || v === "2000-01-01";
  const muted = (v) => (sinFecha(v) ? "td-muted" : undefined);
  const fechaTxt = (v) => (sinFecha(v) ? "—" : v);
  let totalGastosModal = 0;

  return (
    <>
      <Header />
      {user.contenedoresReporte ? (
        <div className="card container col-sm-11 rc-page">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <span className="page-title-icon"><i className="fas fa-chart-line" /></span>
                {t("Reporte Contenedores")}
              </h3>
              <p className="page-subtitle">{t("Seguimiento, servicios, pagos y saldos por contenedor")}</p>
            </div>
            <div className="page-actions">
              <Button size="sm" color="success" href="/ContenedoresCreate">
                <i className="fas fa-plus" /> {t("Nuevo")}
              </Button>
              <Button size="sm" href="/MenuReportes" color="secondary" id="botonListado">
                <i className="fas fa-arrow-left" /> {t("Regresar")}
              </Button>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" color="danger" onClick={PDFTabla} disabled={!totalItems}>
                  <i className="far fa-file-pdf"></i> PDF
                </Button>
                <Button size="sm" onClick={toggleMail} color="info" disabled={!totalItems}>
                  <i className="fas fa-envelope"></i> eMail
                </Button>
                <Button size="sm" color="primary" onClick={excel} disabled={!totalItems}>
                  <i className="far fa-file-excel"></i> Excel
                </Button>
              </ButtonGroup>
            </div>
          </div>

          <Modal size="sm" isOpen={modalMail} toggle={toggleMail}>
            <ModalHeader toggle={toggleMail}>{t("Enviar Listado Contenedores")}</ModalHeader>
            <ModalBody>
              <Label className="mr-sm-2">Email</Label>
              <Input className="col-sm-12" type="text" value={mailTo} required onChange={(e) => setMailTo(e.target.value)} />
            </ModalBody>
            <ModalFooter>
              <Button size="sm" color="info" onClick={enviaMail}>
                <i className="fas fa-paper-plane" /> {t("Enviar")}
              </Button>
            </ModalFooter>
          </Modal>

          {/* ── Periodo y filtros ── */}
          <div className="rc-presets">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className={`rc-preset${presetActivo(p) ? " active" : ""}`}
                onClick={() => aplicarPreset(p)}
              >
                {t(p.label)}
              </button>
            ))}
          </div>
          <div className="filter-bar">
            <div>
              <Label>{t("Fecha Inicio")}</Label>
              <Input bsSize="sm" type="date" value={selectedFechaInicio} required
                onChange={(e) => { setSelectedFechaInicio(e.target.value); setCurrentPage(1); }} />
            </div>
            <div>
              <Label>{t("Fecha Fin")}</Label>
              <Input bsSize="sm" type="date" value={selectedFechaFin} required
                onChange={(e) => { setSelectedFechaFin(e.target.value); setCurrentPage(1); }} />
            </div>
            <div>
              <Label>{t("Cliente")}</Label>
              <Input bsSize="sm" type="select" value={selectedCliente}
                onChange={(e) => { setSelectedCliente(e.target.value); setCurrentPage(1); }}>
                <option value="">{t("Todos")}</option>
                {[...clientes]
                  .sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                  .map((a) => <option key={a._id} value={a._id}>{a.nombre_comercial}</option>)}
              </Input>
            </div>
            <div>
              <Label>{t("Broker")}</Label>
              <Input bsSize="sm" type="select" value={selectedBroker}
                onChange={(e) => { setSelectedBroker(e.target.value); setCurrentPage(1); }}>
                <option value="">{t("Todos")}</option>
                {[...brokers]
                  .sort((a, b) => (a.name > b.name ? 1 : -1))
                  .map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
              </Input>
            </div>
            <div>
              <Label>{t("Facturado")}</Label>
              <Input bsSize="sm" type="select" value={selectedFacturado}
                onChange={(e) => { setSelectedFacturado(e.target.value); setCurrentPage(1); }}>
                <option value="">{t("Todos")}</option>
                <option value="Si">{t("Si")}</option>
                <option value="No">{t("No")}</option>
              </Input>
            </div>
            <div>
              <Label>{t("Entregado")}</Label>
              <Input bsSize="sm" type="select" value={selectedEntregado}
                onChange={(e) => { setSelectedEntregado(e.target.value); setCurrentPage(1); }}>
                <option value="">{t("Todos")}</option>
                <option value="Si">{t("Si")}</option>
                <option value="No">{t("No")}</option>
              </Input>
            </div>
            {hayFiltros ? (
              <div style={{ justifyContent: "flex-end" }}>
                <Button size="sm" color="secondary" onClick={limpiarFiltros}>
                  <i className="fas fa-eraser" /> {t("Limpiar filtros")}
                </Button>
              </div>
            ) : null}
          </div>

          {/* ── Indicadores ── */}
          <div className={`rc-kpis${cargando ? " rc-loading" : ""}`}>
            <div className="rc-kpi">
              <div className="rc-kpi-icon" style={{ background: "#dbeafe", color: "#1d4ed8" }}><i className="fas fa-ship" /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{t("Contenedores")}</div>
                <div className="rc-kpi-value">{fmtNum(totalItems)}</div>
                <div className="rc-kpi-sub">{fmtNum(resumen.entregados)} {t("entregados")} · {fmtNum(enProceso)} {t("en proceso")}</div>
              </div>
            </div>
            <div className="rc-kpi">
              <div className="rc-kpi-icon" style={{ background: "#ede9fe", color: "#6d28d9" }}><i className="fas fa-boxes" /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{t("Piezas")}</div>
                <div className="rc-kpi-value">{fmtNum(Math.round(resumen.piezas))}</div>
                <div className="rc-kpi-sub">{t("Flete")} {fmtMoneyCorto(resumen.flete)}</div>
              </div>
            </div>
            <div className="rc-kpi">
              <div className="rc-kpi-icon" style={{ background: "#dcfce7", color: "#15803d" }}><i className="fas fa-file-invoice-dollar" /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{t("Facturado")}</div>
                <div className="rc-kpi-value" title={fmtMoney(resumen.servicio)}>{fmtMoneyCorto(resumen.servicio)}</div>
                <div className="rc-kpi-sub">{t("Promedio")} {fmtMoneyCorto(totalItems ? resumen.servicio / totalItems : 0)}</div>
              </div>
            </div>
            <div className="rc-kpi">
              <div className="rc-kpi-icon" style={{ background: "#cffafe", color: "#0e7490" }}><i className="fas fa-hand-holding-usd" /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{t("Cobrado")}</div>
                <div className="rc-kpi-value" title={fmtMoney(resumen.pagos)}>{fmtMoneyCorto(resumen.pagos)}</div>
                <div className="rc-progress"><span style={{ width: `${pctCobrado}%` }} /></div>
                <div className="rc-kpi-sub">{pctCobrado}% {t("de lo facturado")}</div>
              </div>
            </div>
            <div className="rc-kpi">
              <div className="rc-kpi-icon" style={{ background: "#fee2e2", color: "#b91c1c" }}><i className="fas fa-balance-scale" /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{t("Saldo por cobrar")}</div>
                <div className="rc-kpi-value" style={{ color: resumen.saldo > 0 ? "#b91c1c" : undefined }} title={fmtMoney(resumen.saldo)}>
                  {fmtMoneyCorto(resumen.saldo)}
                </div>
                <div className="rc-kpi-sub">{fmtNum(filtrados.filter((c) => c.saldo > 0).length)} {t("contenedores con saldo")}</div>
              </div>
            </div>
            <div className="rc-kpi">
              <div className="rc-kpi-icon" style={{ background: "#ffedd5", color: "#c2410c" }}><i className="fas fa-coins" /></div>
              <div className="rc-kpi-body">
                <div className="rc-kpi-label">{t("Costos de importación")}</div>
                <div className="rc-kpi-value" title={fmtMoney(resumen.gastos)}>{fmtMoneyCorto(resumen.gastos)}</div>
                <div className="rc-kpi-sub">{fmtNum(filtrados.filter((c) => c.totalGastos > 0).length)} {t("con costos registrados")}</div>
              </div>
            </div>
          </div>

          {/* ── Gráficas ── */}
          {!cargando && totalItems > 0 ? (
            <div className="rc-insights">
              <div className="rc-card">
                <div className="rc-card-title"><i className="fas fa-trophy" /> {t("Top clientes")}</div>
                {resumen.topClientes.map(([nombre, d], i) => (
                  <div key={nombre} className="rc-bar-row">
                    <div className="rc-bar-label">
                      <span className="rc-rank">{i + 1}</span>{nombre}
                      <div className="rc-bar-sub">{d.monto > 0 ? `${fmtMoneyCorto(d.monto)} ${t("facturado")}` : t("Sin facturar")}</div>
                    </div>
                    <div className="rc-bar"><span style={{ width: `${pct(d.n, maxCliente)}%` }} /></div>
                    <div className="rc-bar-value">{fmtNum(d.n)} <small>{t("cont.")}</small></div>
                  </div>
                ))}
              </div>
              <div className="rc-card rc-card-center">
                <div className="rc-card-title"><i className="fas fa-truck" /> {t("Estatus de entrega")}</div>
                <div className="rc-donut" style={{ background: `conic-gradient(#16a34a 0 ${pctEntregados}%, #f59e0b ${pctEntregados}% 100%)` }}>
                  <div className="rc-donut-hole">
                    <strong>{pctEntregados}%</strong>
                    <span>{t("entregados")}</span>
                  </div>
                </div>
                <div className="rc-legend">
                  <span><i style={{ background: "#16a34a" }} /> {t("Entregados")} {fmtNum(resumen.entregados)}</span>
                  <span><i style={{ background: "#f59e0b" }} /> {t("En proceso")} {fmtNum(enProceso)}</span>
                </div>
              </div>
              <div className="rc-card">
                <div className="rc-card-title"><i className="fas fa-calendar-alt" /> {t("Contenedores por mes")}</div>
                <div className="rc-columns">
                  {resumen.meses.map(([mes, n]) => (
                    <div key={mes} className="rc-col" title={`${mes}: ${n}`}>
                      <div className="rc-col-value">{n}</div>
                      <div className="rc-col-bar"><span style={{ height: `${Math.max(4, pct(n, maxMes))}%` }} /></div>
                      <div className="rc-col-label">{MESES_CORTOS[Number(mes.slice(5, 7)) - 1]}<br /><small>{mes.slice(2, 4)}</small></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {/* ── Tabla ── */}
          <div className="table-toolbar">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
            <Search
              onSearch={(value) => {
                setSearch(safeText(value).split(",").map((term) => term.trim()).filter(Boolean));
                setCurrentPage(1);
              }}
            />
          </div>

          {cargando ? (
            <div className="rc-state">
              <i className="fas fa-spinner fa-spin" />
              <div>{t("Cargando contenedores del periodo…")}</div>
            </div>
          ) : totalItems === 0 ? (
            <div className="rc-state">
              <i className="fas fa-box-open" />
              <div>{t("No hay contenedores con estos filtros")}</div>
              {hayFiltros ? (
                <Button size="sm" color="secondary" onClick={limpiarFiltros}><i className="fas fa-eraser" /> {t("Limpiar filtros")}</Button>
              ) : null}
            </div>
          ) : (
            <Table size="sm" hover className="table-responsive-xl">
              <TableHeader headers={headers} onSorting={(field, order) => setSorting({ field, order })} />
              <tbody>
                {commentsData.map((a) => {
                  const cobrado = pct(a.pagos, a.montoServicio);
                  return (
                    <tr key={a._id}>
                      <td>{a.fecha}</td>
                      <td className="td-strong">{a.numeroReferencia}</td>
                      <td className={muted(a.referenciaExtra)}>{a.referenciaExtra}</td>
                      <td className="td-strong">{a.referencia}</td>
                      <td>
                        <span className={`badge-soft badge-soft-${a.entregado === "Si" ? "success" : "warning"}`}>
                          {a.entregado === "Si" ? t("Entregado") : t("En proceso")}
                        </span>
                      </td>
                      <td>{a.clientes}</td>
                      <td>{a.forwarders}</td>
                      <td className={muted(a.etd)}>{fechaTxt(a.etd)}</td>
                      <td className={muted(a.etaReal)}>{fechaTxt(a.etaReal)}</td>
                      <td>{a.brokers}</td>
                      <td className="td-num">{fmtNum(a.totalCantidad)}</td>
                      <td className={`rc-arts${a.articulosResumen ? "" : " td-muted"}`} title={a.articulosResumen}><div>{a.articulosResumen || "—"}</div></td>
                      <td className={muted(a.fechaEntregaReal)}>{fechaTxt(a.fechaEntregaReal)}</td>
                      <td className="td-num td-money">{fmtMoney(a.flete)}</td>
                      <td className="td-num td-money">{fmtMoney(a.totalGastos)}</td>
                      <td>
                        <span className={`badge-soft badge-soft-${a.facturado === "Si" ? "success" : "warning"}`}>{a.facturadoFacturas}</span>
                      </td>
                      <td className="td-num td-money">{fmtMoney(a.montoServicio)}</td>
                      <td className="td-num td-money">
                        {fmtMoney(a.pagos)}
                        {a.montoServicio > 0 ? (
                          <div className="rc-mini-progress" title={`${cobrado}% ${t("cobrado")}`}><span style={{ width: `${cobrado}%` }} /></div>
                        ) : null}
                      </td>
                      <td className={muted(a.fechaPago)}>{fechaTxt(a.fechaPago)}</td>
                      <td className="td-num">
                        {a.saldo > 0 ? (
                          <span className="rc-saldo">{fmtMoney(a.saldo)}</span>
                        ) : a.montoServicio > 0 ? (
                          <span className="badge-soft badge-soft-success">{t("Pagado")}</span>
                        ) : (
                          <span className="td-muted">{fmtMoney(a.saldo)}</span>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          color="danger"
                          title={t("Detalle de piezas (PDF)")}
                          onClick={() => PDFOC(a.fecha, a.totalCantidad, a.numero, a.naviera, a._id)}
                        >
                          <i className="far fa-file-pdf"></i>
                        </Button>
                      </td>
                      <td>
                        <Button color="primary" size="sm" title={t("Fotos")} onClick={() => jalaFoto(a._id, a.surtidoId)}>
                          <i className="fas fa-camera"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="total-row">
                  <td colSpan={14} className="negrita" align="right">TOTAL ({fmtNum(totalItems)})</td>
                  <td className="negrita td-num td-money">{fmtMoney(resumen.gastos)}</td>
                  <td></td>
                  <td className="negrita td-num td-money">{fmtMoney(resumen.servicio)}</td>
                  <td className="negrita td-num td-money">{fmtMoney(resumen.pagos)}</td>
                  <td></td>
                  <td className="negrita td-num td-money">{fmtMoney(resumen.saldo)}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </Table>
          )}

          {!cargando && totalItems > ITEMS_PER_PAGE ? (
            <div className="table-toolbar">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          ) : null}
        </div>
      ) : undefined}

      <Modal size="xxl" isOpen={modalFoto} toggle={toggleFoto}>
        <ModalHeader toggle={toggleFoto}>{t("Fotos")}</ModalHeader>
        <ModalBody>
          <Row>
            {(photo || []).length > 0 ? (
              (photo || []).map((item) =>
                item.format === "pdf" ? (
                  <h6 key={item.asset_id || item.id} style={{ textAlign: "center" }}>
                    <embed src={item.secure_url} type="application/pdf" width="500" height="600" />
                  </h6>
                ) : (
                  <h6 key={item.asset_id || item.id} style={{ textAlign: "center" }}>
                    <img src={item.secure_url} alt="Uploaded" width="250" height="auto" />
                  </h6>
                )
              )
            ) : (
              <h4>No se ha subido una Foto o PDF....</h4>
            )}
          </Row>
        </ModalBody>
      </Modal>

      <Modal size="xxl" isOpen={modalGastos} toggle={toggleGastos}>
        <ModalHeader toggle={toggleGastos}>{t("Gastos")}</ModalHeader>
        <ModalBody>
          <div className="card container">
            <Table size="sm" hover className="table-responsive-xl">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Descripcion</th>
                  <th className="td-num">{t("Monto")}</th>
                </tr>
              </thead>
              <tbody>
                {gastos.map((c) => {
                  totalGastosModal = totalGastosModal + c.total;
                  return (
                    <tr key={c._id}>
                      <td>{c.fecha}</td>
                      <td>{c.conceptosGastos[0].name}</td>
                      <td className="td-num td-money">{fmtMoney(c.total)}</td>
                    </tr>
                  );
                })}
                <tr className="total-row">
                  <td></td>
                  <td className="negrita">{t("Total")}</td>
                  <td className="negrita td-num td-money">{fmtMoney(totalGastosModal)}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default ReporteContenedores;
